import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks, Task } from '../../crm-tasks/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import GenericListWidget from './GenericListWidget';
import type { DashboardScope } from '../contexts/DashboardFiltersContext';

export interface OverdueTasksProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  scope?: DashboardScope;
}

const today = new Date().toISOString().slice(0, 10);

const OverdueTasks: React.FC<OverdueTasksProps> = ({ limit = 5, embedded, settings, scope = 'myWork' }) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { user } = useAuth();
  const { tasks, isLoading } = useTasks();

  const items = React.useMemo(() => {
    const arr = Array.isArray(tasks) ? tasks : [];
    return arr
      .filter((t: Task) => {
        if (scope === 'myWork' && user?.id != null && t.taskAssignedTo !== user.id) return false;
        const status = t.taskStatusDescr?.ts_descr?.toLowerCase() ?? '';
        const completed = status.includes('completed') || status.includes('done');
        return t.taskDate && t.taskDate < today && !completed;
      })
      .sort((a: Task, b: Task) => {
        const da = new Date(a.taskDate || 0).getTime();
        const db = new Date(b.taskDate || 0).getTime();
        return da - db;
      })
      .slice(0, effectiveLimit);
  }, [tasks, effectiveLimit, scope, user?.id]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<Task>
      title="Overdue Tasks"
      viewAllPath="/crm_tasks"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No overdue tasks"
      getItemKey={(t) => t.taskId}
      embedded={embedded}
      renderItem={(task) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/crm_tasks/${task.taskId}`)}
          onKeyDown={(e) => e.key === 'Enter' && navigate(`/crm_tasks/${task.taskId}`)}
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0 bg-red-50/50 dark:bg-red-900/10"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2`}>
              {task.taskDescription || task.taskSubject || 'Untitled Task'}
            </p>
            <p className="text-[10px] sm:text-xs text-red-600 dark:text-red-400 mt-1">
              Due: {task.taskDate ? new Date(task.taskDate).toLocaleDateString('en-US') : ''}
              {task.client?.clientName && ` · ${task.client.clientName}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default OverdueTasks;
